import { useEffect, useMemo, useState, useCallback } from 'react';
import i18nInst from '@shared/i18n';
import { useCloseOnTabSwitch } from '@shared/hooks/useCloseOnTabSwitch';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary, NamedIcon } from '@shared/ui';
import { tierColor, formatScore } from '@shared/lib/score';

// Persistence — survives page refresh. Same key is read by the Map view
// to highlight optimized boards (search BRIEF_STORAGE_KEY in
// BrowseOutdoorAnalytics.tsx).
export const BRIEF_STORAGE_KEY = 'kgdb-outdoor-brief-v1';

interface PersistedBrief {
  form?: {
    city?: string;
    audienceTargets?: string[];
    budgetKgs?: string;
    durationMonths?: string;
    minBoards?: string;
    maxBoards?: string;
    minAudienceMatch?: string;
    minSpreadKm?: string;
    constructionTypes?: string[];
    nearBrand?: { id: string; name: string } | null;
    nearRadiusM?: string;
    avoidBrands?: Array<{ id: string; name: string }>;
    avoidRadiusM?: string;
  };
  results?: unknown[];
  hasRunOnce?: boolean;
  at?: number;
}

function loadPersistedBrief(): PersistedBrief {
  try {
    const raw = localStorage.getItem(BRIEF_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PersistedBrief;
  } catch {
    return {};
  }
}

function savePersistedBrief(state: PersistedBrief): void {
  try {
    localStorage.setItem(BRIEF_STORAGE_KEY, JSON.stringify({ ...state, at: Date.now() }));
  } catch {
    /* quota / private mode — ignore */
  }
}

// ─── Types ───────────────────────────────────────────────────────────
interface OptimizeResult {
  board_id: string;
  city: string | null;
  address: string | null;
  construction_type: string | null;
  score_total: number | string | null;
  relevance_score: number | string | null;
  sales_price_kgs: number | string | null;
  ots: number | string | null;
  ots_calculated: number | string | null;
  provider: string | null;
  osm_pois: number | null;
  twogis_pois: number | null;
  google_pois: number | null;
  target_poi_count: number | null;
  board_lat: number | null;
  board_lng: number | null;
  audience_match_score: number | string | null;
  why_this_board: {
    audience_match?: number | null;
    audience_targets?: string[] | null;
    target_poi_count?: number | null;
    near_brand_dist_m?: number | null;
    avoid_brand_dist_m?: number | null;
    score_total?: number | null;
    ots_calculated?: number | null;
  } | null;
}

const N = (v: number | string | null | undefined): number =>
  v == null ? 0 : Number(v) || 0;

const compactNumber = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return Math.round(n).toLocaleString(i18nInst.language);
};

// 18 distinct audience categories backed by mv_board_audience_profile.
// Order is intentional: housing first (most boards have residential signal),
// then work/study, then shopping, then services and leisure.
// `key` doubles as the lookup into ICONS_BY_KEY for the bespoke themed icon.
const AUDIENCE_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'private_homes',     label: 'Private homes' },
  { key: 'low_rise',          label: 'Low-rise' },
  { key: 'high_rise',         label: 'High-rise' },
  { key: 'workers',           label: 'Workers' },
  { key: 'university',        label: 'University' },
  { key: 'schoolchildren',    label: 'Schoolchildren' },
  { key: 'preschool_parents', label: 'Pre-school parents' },
  { key: 'daily_shoppers',    label: 'Daily shoppers' },
  { key: 'mall_goers',        label: 'Mall-goers' },
  { key: 'pharmacy',          label: 'Pharmacy' },
  { key: 'patients',          label: 'Patients' },
  { key: 'beauty',            label: 'Beauty' },
  { key: 'fitness',           label: 'Fitness' },
  { key: 'nightlife',         label: 'Nightlife' },
  { key: 'foodies',           label: 'Foodies' },
  { key: 'car_owners',        label: 'Car owners' },
  { key: 'tourists',          label: 'Tourists' },
  { key: 'parks',             label: 'Parks & public' },
];

interface Props {
  onSwitchToMap: () => void;
}

export default function BrowseOutdoorBrief({ onSwitchToMap }: Props) {
  const { t } = useTranslation('customer');

  // ─── Brief form state ───────────────────────────────────────────────
  // Lazy-initialize from localStorage so a page refresh doesn't lose
  // either the input form or the last optimizer run.
  const persisted = useMemo(() => loadPersistedBrief(), []);
  const pf = persisted.form ?? {};

  const [city, setCity] = useState<string>(pf.city ?? 'Бишкек');
  const [audienceTargets, setAudienceTargets] = useState<string[]>(pf.audienceTargets ?? []);
  const [budgetKgs, setBudgetKgs] = useState<string>(pf.budgetKgs ?? '500000');
  const [durationMonths, setDurationMonths] = useState<string>(pf.durationMonths ?? '1');
  const [minBoards, setMinBoards] = useState<string>(pf.minBoards ?? '5');
  const [maxBoards, setMaxBoards] = useState<string>(pf.maxBoards ?? '15');
  const [minAudienceMatch, setMinAudienceMatch] = useState<string>(pf.minAudienceMatch ?? '0');
  const [minSpreadKm, setMinSpreadKm] = useState<string>(pf.minSpreadKm ?? '0.3');
  const [constructionTypes, setConstructionTypes] = useState<string[]>(pf.constructionTypes ?? []);

  // Brand context (optional)
  const [nearBrand, setNearBrand] = useState<{ id: string; name: string } | null>(pf.nearBrand ?? null);
  const [nearRadiusM, setNearRadiusM] = useState<string>(pf.nearRadiusM ?? '500');
  const [avoidBrands, setAvoidBrands] = useState<Array<{ id: string; name: string }>>(pf.avoidBrands ?? []);
  const [avoidRadiusM, setAvoidRadiusM] = useState<string>(pf.avoidRadiusM ?? '200');

  const [results, setResults] = useState<OptimizeResult[]>(
    Array.isArray(persisted.results) ? (persisted.results as OptimizeResult[]) : [],
  );
  const [optimizing, setOptimizing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasRunOnce, setHasRunOnce] = useState<boolean>(persisted.hasRunOnce === true);

  // Persist on any state change so a refresh / mode-switch round-trip
  // never loses the user's brief or the last result set.
  useEffect(() => {
    savePersistedBrief({
      form: {
        city, audienceTargets, budgetKgs, durationMonths, minBoards, maxBoards,
        minAudienceMatch, minSpreadKm, constructionTypes,
        nearBrand, nearRadiusM, avoidBrands, avoidRadiusM,
      },
      results,
      hasRunOnce,
    });
  }, [
    city, audienceTargets, budgetKgs, durationMonths, minBoards, maxBoards,
    minAudienceMatch, minSpreadKm, constructionTypes,
    nearBrand, nearRadiusM, avoidBrands, avoidRadiusM,
    results, hasRunOnce,
  ]);

  const citiesQ = useSupabaseQuery<{ city: string | null }[]>(
    ['outdoor-cities-brief'],
    async () =>
      supabase
        .from('v_outdoor_boards_customer')
        .select('city')
        .eq('is_active', true)
        .not('city', 'is', null)
        .limit(2000),
  );

  const cities = useMemo(() => {
    const set = new Set<string>();
    for (const r of citiesQ.data ?? []) if (r.city) set.add(r.city);
    return Array.from(set).sort();
  }, [citiesQ.data]);

  const constructionTypesQ = useSupabaseQuery<{ construction_type: string | null }[]>(
    ['outdoor-types-brief'],
    async () =>
      supabase
        .from('v_outdoor_boards_customer')
        .select('construction_type')
        .eq('is_active', true)
        .not('construction_type', 'is', null)
        .limit(2000),
  );

  const allTypes = useMemo(() => {
    const set = new Set<string>();
    for (const r of constructionTypesQ.data ?? []) if (r.construction_type) set.add(r.construction_type);
    return Array.from(set).sort();
  }, [constructionTypesQ.data]);

  // ─── Optimize handler ──────────────────────────────────────────────
  const runOptimize = async () => {
    setError(null);
    setOptimizing(true);
    setHasRunOnce(true);
    try {
      const params: Record<string, unknown> = {
        p_max_budget: parseFloat(budgetKgs) || 0,
        p_duration_months: parseInt(durationMonths, 10) || 1,
        p_min_boards: parseInt(minBoards, 10) || 1,
        p_max_boards: parseInt(maxBoards, 10) || 200,
        p_min_spread_km: parseFloat(minSpreadKm) || 0,
      };
      if (city) params.p_city = city;
      if (audienceTargets.length > 0) {
        params.p_audience_targets = audienceTargets;
        const minMatch = parseInt(minAudienceMatch, 10);
        if (Number.isFinite(minMatch) && minMatch > 0) params.p_min_audience_match = minMatch;
      }
      if (constructionTypes.length > 0) params.p_types = constructionTypes;

      if (nearBrand) {
        params.p_near_brand_id = nearBrand.id;
        const radius = parseInt(nearRadiusM, 10);
        if (Number.isFinite(radius) && radius > 0) params.p_near_radius_m = radius;
      }
      if (avoidBrands.length > 0) {
        params.p_avoid_brand_ids = avoidBrands.map((b) => b.id);
        const radius = parseInt(avoidRadiusM, 10);
        if (Number.isFinite(radius) && radius > 0) params.p_avoid_radius_m = radius;
      }

      const { data, error: rpcErr } = await supabase.rpc('optimize_campaign', params);
      if (rpcErr) {
        setError(rpcErr.message);
        setResults([]);
      } else {
        setResults((data ?? []) as OptimizeResult[]);
        // Phone: the results sit under a long form — bring them into view.
        if (window.matchMedia('(max-width: 760px)').matches) {
          setTimeout(() => document.getElementById('brief-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setResults([]);
    } finally {
      setOptimizing(false);
    }
  };

  const toggleAudience = (key: string) => {
    setAudienceTargets((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleConstructionType = (k: string) => {
    setConstructionTypes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  };

  const totals = useMemo(() => {
    const months = parseInt(durationMonths, 10) || 1;
    const total = results.reduce((sum, r) => sum + N(r.sales_price_kgs) * months, 0);
    const totalOts = results.reduce((s, r) => s + (N(r.ots) || N(r.ots_calculated)), 0);
    const avgMatch =
      results.length > 0
        ? results.reduce((s, r) => s + N(r.audience_match_score), 0) / results.length
        : 0;
    return { boards: results.length, total, totalOts, avgMatch };
  }, [results, durationMonths]);

  return (
    <SectionErrorBoundary>
      <div style={{ padding: 'clamp(0px, 2vw, 20px) clamp(0px, 2vw, 24px)', maxWidth: 1400, margin: '0 auto' }}>
        {/* Mode toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-semibold)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <NamedIcon name="outdoor" size={22} />
            {t('outdoor.brief.title', { defaultValue: 'Outdoor — Build campaign' })}
          </h2>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="glow-btn-ghost"
            onClick={onSwitchToMap}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <NamedIcon name="arrow_left" size={14} />
            {t('outdoor.brief.switchMap', { defaultValue: 'Map view' })}
          </button>
        </div>

        {/* Split layout: form left, results right. On a phone the fixed 320px form
            column left the results 0px wide — .brief-split stacks them (global.css). */}
        <div className="brief-split">
          {/* ─── Brief form ─── */}
          <div
            style={{
              background: 'rgba(15,45,100,0.08)',
              border: '1px solid rgba(15,45,100,0.3)',
              borderRadius: 10,
              padding: 16,
              position: 'sticky',
              top: 12,
            }}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: 'var(--fs-label)' }}>
              {t('outdoor.brief.form.title', { defaultValue: 'Your campaign brief' })}
            </h3>

            {/* City */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.city', { defaultValue: 'City' })}
              </label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                style={inputStyle}
              >
                {cities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Audience targets */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.audience', { defaultValue: 'Target audience' })}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {AUDIENCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => toggleAudience(opt.key)}
                    style={{
                      padding: '6px 8px',
                      fontSize: 'var(--fs-caption)',
                      textAlign: 'left',
                      background: audienceTargets.includes(opt.key)
                        ? 'rgba(74,158,255,0.25)'
                        : 'rgba(255,255,255,0.04)',
                      border: audienceTargets.includes(opt.key)
                        ? '1px solid rgba(74,158,255,0.5)'
                        : '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 4,
                      color: '#dde',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <NamedIcon name={opt.key} size={14} />
                      {t(`browse.outdoor.audience.${opt.key}`, { defaultValue: opt.label })}
                    </span>
                  </button>
                ))}
              </div>
              {audienceTargets.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ ...labelStyle, marginBottom: 4 }}>
                    {t('outdoor.brief.form.minAudienceMatch', {
                      defaultValue: 'Min audience match (0–100)',
                    })}
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={minAudienceMatch}
                    onChange={(e) => setMinAudienceMatch(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            {/* Budget */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.budget', { defaultValue: 'Budget (KGS)' })}
              </label>
              <input
                type="number"
                value={budgetKgs}
                onChange={(e) => setBudgetKgs(e.target.value)}
                style={inputStyle}
                placeholder="500000"
              />
            </div>

            {/* Duration */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.duration', { defaultValue: 'Duration (months)' })}
              </label>
              <input
                type="number"
                min={1}
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Boards count */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.boards', { defaultValue: 'Boards (min — max)' })}
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="number"
                  min={1}
                  value={minBoards}
                  onChange={(e) => setMinBoards(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ alignSelf: 'center', color: '#9aa' }}>–</span>
                <input
                  type="number"
                  min={1}
                  value={maxBoards}
                  onChange={(e) => setMaxBoards(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            </div>

            {/* Spread */}
            <div style={fieldRow}>
              <label style={labelStyle}>
                {t('outdoor.brief.form.spread', { defaultValue: 'Min spread (km)' })}
              </label>
              <input
                type="number"
                step="0.1"
                min={0}
                value={minSpreadKm}
                onChange={(e) => setMinSpreadKm(e.target.value)}
                style={inputStyle}
              />
            </div>

            {/* Construction types */}
            {allTypes.length > 0 && (
              <div style={fieldRow}>
                <label style={labelStyle}>
                  {t('outdoor.brief.form.types', { defaultValue: 'Format (optional)' })}
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {allTypes.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleConstructionType(k)}
                      style={{
                        padding: '3px 8px',
                        fontSize: 'var(--fs-micro)',
                        background: constructionTypes.includes(k)
                          ? 'rgba(74,158,255,0.25)'
                          : 'rgba(255,255,255,0.04)',
                        border: constructionTypes.includes(k)
                          ? '1px solid rgba(74,158,255,0.5)'
                          : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 12,
                        color: '#dde',
                        cursor: 'pointer',
                      }}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Near my brand (single pick) */}
            <div style={fieldRow}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
                <NamedIcon name="target" size={12} />
                {t('outdoor.brief.form.nearBrand', { defaultValue: 'Near my brand (optional)' })}
              </label>
              {nearBrand ? (
                <BrandChip brand={nearBrand} onRemove={() => setNearBrand(null)} accent="#58c896" />
              ) : (
                <BrandSearch
                  placeholder={t('outdoor.brief.form.nearBrandPlaceholder', {
                    defaultValue: 'Search brand…',
                  })}
                  excludeIds={[]}
                  onPick={(b) => setNearBrand(b)}
                />
              )}
              {nearBrand && (
                <div style={{ marginTop: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: 2 }}>
                    {t('outdoor.brief.form.nearRadius', { defaultValue: 'Within (m)' })}
                  </label>
                  <input
                    type="number"
                    min={50}
                    step={50}
                    value={nearRadiusM}
                    onChange={(e) => setNearRadiusM(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            {/* Avoid competitors (multi pick) */}
            <div style={fieldRow}>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
                <NamedIcon name="warning" size={12} />
                {t('outdoor.brief.form.avoidBrand', {
                  defaultValue: 'Avoid competitors (optional)',
                })}
              </label>
              {avoidBrands.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                  {avoidBrands.map((b) => (
                    <BrandChip
                      key={b.id}
                      brand={b}
                      onRemove={() =>
                        setAvoidBrands((prev) => prev.filter((x) => x.id !== b.id))
                      }
                      accent="#f59e0b"
                    />
                  ))}
                </div>
              )}
              <BrandSearch
                placeholder={t('outdoor.brief.form.avoidBrandPlaceholder', {
                  defaultValue: 'Search competitor…',
                })}
                excludeIds={avoidBrands.map((b) => b.id).concat(nearBrand ? [nearBrand.id] : [])}
                onPick={(b) => setAvoidBrands((prev) => [...prev, b])}
              />
              {avoidBrands.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: 2 }}>
                    {t('outdoor.brief.form.avoidRadius', { defaultValue: 'Exclude within (m)' })}
                  </label>
                  <input
                    type="number"
                    min={50}
                    step={50}
                    value={avoidRadiusM}
                    onChange={(e) => setAvoidRadiusM(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={runOptimize}
              disabled={optimizing}
              className="glow-btn"
              style={{ width: '100%', marginTop: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              {optimizing ? (
                t('outdoor.brief.form.optimizing', { defaultValue: 'Optimizing…' })
              ) : (
                <>
                  <NamedIcon name="target" size={15} />
                  {t('outdoor.brief.form.optimize', { defaultValue: 'Find optimal boards' })}
                </>
              )}
            </button>
          </div>

          {/* ─── Results panel ─── */}
          <div id="brief-results">
            {/* Totals strip */}
            {results.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 45%), 1fr))',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <KPI
                  label={t('outdoor.brief.kpi.boards', { defaultValue: 'Boards' })}
                  value={totals.boards.toString()}
                />
                <KPI
                  label={t('outdoor.brief.kpi.totalCost', { defaultValue: 'Total cost' })}
                  value={`${compactNumber(totals.total)} KGS`}
                  hint={`${durationMonths}${t('outdoor.brief.kpi.moSuffix', { defaultValue: 'mo' })}`}
                />
                <KPI
                  label={t('outdoor.brief.kpi.combinedOts', { defaultValue: 'Combined OTS' })}
                  value={compactNumber(totals.totalOts)}
                />
                {audienceTargets.length > 0 && (
                  <KPI
                    label={t('outdoor.brief.kpi.avgMatch', { defaultValue: 'Avg match' })}
                    value={`${totals.avgMatch.toFixed(0)}%`}
                    valueColor={
                      totals.avgMatch >= 75
                        ? '#58c896'
                        : totals.avgMatch >= 50
                        ? '#f5b700'
                        : '#9aa'
                    }
                  />
                )}
              </div>
            )}

            {/* Result cards */}
            {error && (
              <div
                style={{
                  padding: 12,
                  background: 'rgba(220,40,40,0.15)',
                  border: '1px solid rgba(220,40,40,0.4)',
                  borderRadius: 8,
                  color: '#ffb',
                  fontSize: 'var(--fs-body)',
                  marginBottom: 12,
                }}
              >
                {t('outdoor.brief.error', { defaultValue: 'Optimization failed' })}: {error}
              </div>
            )}

            {optimizing && results.length === 0 ? (
              <Skeleton height={300} />
            ) : !hasRunOnce ? (
              <div
                style={{
                  padding: 32,
                  textAlign: 'center',
                  color: '#9aa',
                  fontSize: 'var(--fs-body)',
                  background: 'rgba(15,45,100,0.05)',
                  border: '1px dashed rgba(15,45,100,0.3)',
                  borderRadius: 10,
                }}
              >
                {t('outdoor.brief.empty', {
                  defaultValue:
                    'Set your audience, budget, and constraints, then click "Find optimal boards" to get a ranked recommendation.',
                })}
              </div>
            ) : results.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: '#9aa',
                  fontSize: 'var(--fs-body)',
                  background: 'rgba(15,45,100,0.05)',
                  border: '1px solid rgba(15,45,100,0.2)',
                  borderRadius: 10,
                }}
              >
                {t('outdoor.brief.noResults', {
                  defaultValue:
                    'No boards match your brief. Try widening the budget, lowering min match, or removing some filters.',
                })}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {results.map((r, i) => (
                  <ResultCard
                    key={r.board_id}
                    rank={i + 1}
                    r={r}
                    months={parseInt(durationMonths, 10) || 1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionErrorBoundary>
  );
}

// ─── Result card ─────────────────────────────────────────────────────
function ResultCard({
  rank,
  r,
  months,
}: {
  rank: number;
  r: OptimizeResult;
  months: number;
}) {
  const { t } = useTranslation('customer');
  const score = N(r.score_total);
  const match = N(r.audience_match_score);
  // Prefer v2-derived OTS from why_this_board.ots_calculated (which is
  // estimate_board_ots_v2 composite when p_use_ots_v2=true). Fall back to
  // provider OTS, then legacy ots_calculated.
  const why = r.why_this_board ?? {};
  const otsV2 = N(why.ots_calculated as number | null);
  const ots = otsV2 || N(r.ots) || N(r.ots_calculated);
  const monthly = N(r.sales_price_kgs);
  const total = monthly * months;
  const reasons = buildReasons(r, t);

  return (
    <div
      className={`glow-card ${rank <= 3 ? 'is-featured' : ''}`}
      style={{
        padding: 12,
        display: 'grid',
        gridTemplateColumns: '40px 1fr auto',
        gap: 12,
        alignItems: 'start',
      }}
    >
      {/* Rank */}
      <div
        style={{
          fontSize: 'var(--fs-h2)',
          fontWeight: 'var(--fw-bold)',
          color: rank === 1 ? '#f5b700' : '#9aa',
          textAlign: 'center',
        }}
      >
        {rank === 1 ? <NamedIcon name="trophy" size={18} color="#f5b700" /> : `#${rank}`}
      </div>

      {/* Body */}
      <div>
        <div style={{ fontSize: 'var(--fs-caption)', color: '#9aa', marginBottom: 2 }}>
          {r.city ?? '—'} · {r.construction_type ?? '—'} · {r.provider ?? '—'}
        </div>
        <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)', marginBottom: 6 }}>
          {r.address ?? '—'}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            fontSize: 'var(--fs-caption)',
            color: '#bcd0ff',
            flexWrap: 'wrap',
            marginBottom: 6,
          }}
        >
          <span>
            {t('outdoor.brief.card.score', { defaultValue: 'Score' })}{' '}
            <b style={{ color: tierColor(score) }}>{formatScore(score)}</b>
          </span>
          {ots > 0 && (
            <span>
              OTS <b>{compactNumber(ots)}</b>
            </span>
          )}
          {match > 0 && (
            <span>
              {t('outdoor.brief.card.match', { defaultValue: 'Match' })}{' '}
              <b
                style={{
                  color:
                    match >= 75
                      ? '#58c896'
                      : match >= 50
                      ? '#f5b700'
                      : '#9aa',
                }}
              >
                {match.toFixed(0)}%
              </b>
            </span>
          )}
        </div>
        {reasons.length > 0 && (
          <ul style={{ margin: '4px 0 0', padding: '0 0 0 16px', fontSize: 'var(--fs-caption)', color: '#9aa' }}>
            {reasons.map((reason, i) => (
              <li key={i} style={{ lineHeight: 1.4 }}>
                {reason}
              </li>
            ))}
          </ul>
        )}
        {why.near_brand_dist_m != null && (
          <div style={{ fontSize: 'var(--fs-micro)', color: '#7c5', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <NamedIcon name="check" size={11} color="#7c5" /> near your brand: {why.near_brand_dist_m}m
          </div>
        )}
        {why.avoid_brand_dist_m != null && why.avoid_brand_dist_m < 1000 && (
          <div style={{ fontSize: 'var(--fs-micro)', color: '#f5b700', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
            <NamedIcon name="warning" size={11} color="#f5b700" /> closest competitor: {why.avoid_brand_dist_m}m
          </div>
        )}
      </div>

      {/* Right column: price */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 'var(--fs-label)', fontWeight: 'var(--fw-bold)', color: '#bcd0ff' }}>
          {compactNumber(monthly)} KGS
        </div>
        <div style={{ fontSize: 'var(--fs-micro)', color: '#9aa' }}>
          /month · {compactNumber(total)} total
        </div>
      </div>
    </div>
  );
}

function buildReasons(
  r: OptimizeResult,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string[] {
  const out: string[] = [];
  const why = r.why_this_board ?? {};
  if (why.audience_match != null && why.audience_targets) {
    const targetLabels = why.audience_targets
      .map((k) => {
        const opt = AUDIENCE_OPTIONS.find((o) => o.key === k);
        return opt
          ? t(`browse.outdoor.audience.${opt.key}`, { defaultValue: opt.label })
          : k;
      })
      .join(' + ');
    out.push(t('outdoor.brief.reason.audienceMatch', {
      defaultValue: '{{pct}}% audience match for {{targets}}',
      pct: why.audience_match,
      targets: targetLabels,
    }));
  }
  if (why.target_poi_count != null && why.target_poi_count > 0) {
    out.push(t('outdoor.brief.reason.targetPois', {
      defaultValue: '{{n}} target POIs within radius',
      n: why.target_poi_count,
    }));
  }
  if (N(r.osm_pois) + N(r.twogis_pois) + N(r.google_pois) > 0) {
    const total = N(r.osm_pois) + N(r.twogis_pois) + N(r.google_pois);
    out.push(t('outdoor.brief.reason.poisNearby', {
      defaultValue: '{{total}} POIs nearby (osm {{osm}}, 2gis {{gis}}, google {{google}})',
      total,
      osm: r.osm_pois,
      gis: r.twogis_pois,
      google: r.google_pois,
    }));
  }
  return out;
}

// ─── KPI tile ───────────────────────────────────────────────────────
function KPI({
  label,
  value,
  hint,
  valueColor,
}: {
  label: string;
  value: string;
  hint?: string;
  valueColor?: string;
}) {
  return (
    <div className="glow-card" style={{ padding: '10px 12px' }}>
      <div
        style={{
          color: '#9aa',
          fontSize: 'var(--fs-micro)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 'var(--fs-label)', fontWeight: 'var(--fw-semibold)', marginTop: 2, color: valueColor ?? undefined }}>
        {value}
      </div>
      {hint && <div style={{ color: '#778', fontSize: 'var(--fs-micro)', marginTop: 1 }}>{hint}</div>}
    </div>
  );
}

// ─── Brand search w/ debounced autocomplete ────────────────────────
function BrandSearch({
  placeholder,
  excludeIds,
  onPick,
}: {
  placeholder: string;
  excludeIds: string[];
  onPick: (b: { id: string; name: string }) => void;
}) {
  const { t } = useTranslation('customer');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Array<{ id: string; name: string; branch_count: number | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const closeDropdown = useCallback(() => setOpen(false), []);
  useCloseOnTabSwitch(closeDropdown);

  const runSearch = async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await supabase
        .from('brands')
        .select('id, name, branch_count')
        .ilike('name', `%${query}%`)
        .order('branch_count', { ascending: false, nullsFirst: false })
        .limit(8);
      const filtered = (data ?? []).filter((b) => !excludeIds.includes(b.id));
      setResults(filtered);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          void runSearch(e.target.value);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={inputStyle}
      />
      {open && q.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 2,
            background: 'rgba(10,15,30,0.97)',
            border: '1px solid rgba(74,158,255,0.4)',
            borderRadius: 4,
            zIndex: 100,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          }}
        >
          {loading ? (
            <div style={{ padding: 8, fontSize: 'var(--fs-caption)', color: '#9aa' }}>Searching…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 8, fontSize: 'var(--fs-caption)', color: '#9aa' }}>
              {t('outdoor.brief.brand.noMatch', { defaultValue: 'No brands match.' })}
            </div>
          ) : (
            results.map((b) => (
              <button
                key={b.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick({ id: b.id, name: b.name });
                  setQ('');
                  setResults([]);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: 'var(--fs-small)',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  color: '#dde',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(74,158,255,0.15)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <span>{b.name}</span>
                {b.branch_count != null && b.branch_count > 0 && (
                  <span style={{ float: 'right', color: '#778', fontSize: 'var(--fs-micro)' }}>
                    {b.branch_count} branches
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BrandChip({
  brand,
  onRemove,
  accent,
}: {
  brand: { id: string; name: string };
  onRemove: () => void;
  accent: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        background: `${accent}22`,
        border: `1px solid ${accent}66`,
        borderRadius: 12,
        fontSize: 'var(--fs-caption)',
        color: '#dde',
      }}
    >
      {brand.name}
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: 'transparent',
          border: 'none',
          color: accent,
          cursor: 'pointer',
          padding: 0,
          fontSize: 'var(--fs-body)',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </span>
  );
}

// ─── Form styles ───────────────────────────────────────────────────
const fieldRow: React.CSSProperties = {
  marginBottom: 10,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--fs-micro)',
  fontWeight: 'var(--fw-semibold)',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#9aa',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  background: 'rgba(15,45,100,0.2)',
  border: '1px solid rgba(15,45,100,0.5)',
  borderRadius: 4,
  color: '#dde',
  fontSize: 'var(--fs-body)',
  fontFamily: 'inherit',
};
